import { useEffect } from 'react'
import { useSetRecoilState } from 'recoil'
import {
  userAtom,
  authTokenAtom,
  branchesAtom,
  selectedBranchAtom,
  isAuthLoadingAtom,
  BUILD_ENVIRONMENT,
  BUILD_API_BASE_URL,
} from '../state/atoms'
import { apiClient } from '../api/client'
import { loadAuthSession, clearAuthSession, type AuthSession } from '../storage'
import type { UserProfileData, Branch } from '../api/types'

// Extended AuthSession for compatibility with API types
interface StoredAuthSession extends Omit<AuthSession, 'user' | 'branches'> {
  user: UserProfileData
  branches: Branch[]
  selectedBranchNid: string
  environment: 'development' | 'production'
}

export function useSessionInit() {
  const setUser = useSetRecoilState(userAtom)
  const setToken = useSetRecoilState(authTokenAtom)
  const setBranches = useSetRecoilState(branchesAtom)
  const setSelectedBranch = useSetRecoilState(selectedBranchAtom)
  const setIsLoading = useSetRecoilState(isAuthLoadingAtom)

  useEffect(() => {
    async function loadSession() {
      try {
        apiClient.setBaseUrl(BUILD_API_BASE_URL)

        // Load session from storage (localStorage in browser, can be extended)
        const session = await loadAuthSession() as StoredAuthSession | null

        if (session) {
          // If session environment doesn't match build environment, clear it
          if (session.environment !== BUILD_ENVIRONMENT) {
            console.warn(
              `Session environment (${session.environment}) mismatches build environment (${BUILD_ENVIRONMENT}). Clearing session.`
            )
            await clearAuthSession()
            return
          }

          console.log('✓ Restoring saved auth session')

          // Synchronously set token and base URL in apiClient BEFORE Recoil state
          apiClient.setToken(session.token)
          apiClient.setBaseUrl(BUILD_API_BASE_URL)

          // Now update Recoil state (effects will fire, but already synced)
          setToken(session.token)

          // Now safe to set branch-related state
          setUser(session.user)
          setBranches(session.branches)

          const branch = session.branches.find((b) => b.nid === session.selectedBranchNid)
          setSelectedBranch(branch || session.branches[0] || null)

          console.log('✓ Session restored successfully')
        } else {
          console.log('No saved session found')
        }
      } catch (error) {
        console.error('Failed to load saved session:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadSession()
  }, []) // Empty deps - run once on mount
}
